"use client"

import React, { useState, useEffect, useRef } from "react"
import { BrowserRouter as Router, Routes, Route, NavLink, useLocation } from "react-router-dom"
import { Monitor, Moon, Sun, Github, Mail, Code, FileText, Layers } from "lucide-react"
import Home from "./pages/Home"
import Latest from "./pages/Latest"
import Projects from "./pages/Projects"
import Contact from "./pages/Contact"
import SourceCode from "./pages/SourceCode"
import "./styles/App.css"

// Grid component for background
const GridBackground = () => {
  return (
    <div className="grid-background">
      {Array.from({ length: 20 }).map((_, i) => (
        <div key={`h-${i}`} className="grid-line grid-line-horizontal" style={{ top: `${(i + 1) * 5}%` }} />
      ))}
      {Array.from({ length: 20 }).map((_, i) => (
        <div key={`v-${i}`} className="grid-line grid-line-vertical" style={{ left: `${(i + 1) * 5}%` }} />
      ))}
    </div>
  )
}

// Layout component to handle page-specific classes
const Layout = ({ children }) => {
  const location = useLocation()
  const isSourcePage = location.pathname === "/source"

  return <div className={`app-container ${isSourcePage ? "source-page" : ""}`}>{children}</div>
}

export function LightCursor() {
  const glowRef = useRef(null)

  useEffect(() => {
    const handleMouseMove = (e) => {
      const glow = glowRef.current
      if (!glow) return
      glow.style.left = `${e.clientX}px`
      glow.style.top = `${e.clientY}px`
    }

    window.addEventListener("mousemove", handleMouseMove)
    return () => window.removeEventListener("mousemove", handleMouseMove)
  }, [])

  return <div className="cursor-glow" ref={glowRef}></div>
}

function App() {
  const [darkMode, setDarkMode] = useState(true)

  useEffect(() => {
    document.body.className = darkMode ? "dark-mode" : "light-mode"
  }, [darkMode])

  return (
    <>
      <LightCursor />
      <Router basename="/portfolio">
        <Layout>
          <div className="matrix-background">
            <MatrixRain darkMode={darkMode} />
          </div>
          <GridBackground />
          <header>
            <div className="header-left">
              <div className="logo">
                <Code className="logo-icon" />
                <span>my_portfolio</span>
              </div>
            </div>
            <nav className="centered-nav">
              <NavLink to="/" className={({ isActive }) => (isActive ? "active" : "")}>
                <Monitor className="nav-icon" />
                <span>Home</span>
              </NavLink>
              <NavLink to="/latest" className={({ isActive }) => (isActive ? "active" : "")}>
                <FileText className="nav-icon" />
                <span>Latest</span>
              </NavLink>
              <NavLink to="/projects" className={({ isActive }) => (isActive ? "active" : "")}>
                <Layers className="nav-icon" />
                <span>Projects</span>
              </NavLink>
              <NavLink to="/contact" className={({ isActive }) => (isActive ? "active" : "")}>
                <Mail className="nav-icon" />
                <span>Contact</span>
              </NavLink>
              <NavLink to="/source" className={({ isActive }) => (isActive ? "active" : "")}>
                <Github className="nav-icon" />
                <span>Source</span>
              </NavLink>
            </nav>
            <div className="header-right">
              <div className="profile-section">
                <img src="profile_pic.png" alt="Profile" className="profile-pic" />
                <span className="profile-name">Yousef Thulqarnain</span>
              </div>
              <button className="theme-toggle" onClick={() => setDarkMode(!darkMode)} aria-label="Toggle theme">
                {darkMode ? <Sun className="theme-icon" /> : <Moon className="theme-icon" />}
              </button>
            </div>
          </header>
          <main>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/latest" element={<Latest />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/source" element={<SourceCode />} />
            </Routes>
          </main>
          <footer>
            <div className="footer-content">
              <div className="footer-left">
                <Code className="footer-logo" />
                {/* <span>DEV_PORTFOLIO</span> */}
              </div>
              <div className="footer-right">
                <p> {new Date().getFullYear()} | neovim, btw</p>
                <div className="social-links">
                  <a href="https://github.com/yousefbh1" target="_blank" rel="noopener noreferrer">
                    <Github className="social-icon" />
                  </a>
                  <a href="mailto:yousefbh@gmail.com">
                    <Mail className="social-icon" />
                  </a>
                </div>
              </div>
            </div>
          </footer>
        </Layout>
      </Router>
    </>
  )
}

// Matrix rain animation component
function MatrixRain({ darkMode }) {
  const canvasRef = React.useRef(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const context = canvas.getContext("2d")

    canvas.width = window.innerWidth
    canvas.height = window.innerHeight

    const fontSize = 14
    const columns = canvas.width / fontSize

    const drops = []
    for (let i = 0; i < columns; i++) {
      drops[i] = 1
    }

    const chars =
      "アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン0123456789"

    function draw() {
      context.fillStyle = darkMode ? "rgba(0, 0, 0, 0.05)" : "rgba(255, 255, 255, 0.05)"
      context.fillRect(0, 0, canvas.width, canvas.height)

      context.fillStyle = darkMode ? "#0f0" : "#090"
      context.font = `${fontSize}px monospace`

      for (let i = 0; i < drops.length; i++) {
        const text = chars[Math.floor(Math.random() * chars.length)]
        context.fillText(text, i * fontSize, drops[i] * fontSize)

        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) {
          drops[i] = 0
        }

        drops[i]++
      }
    }

    const interval = setInterval(draw, 33)

    const handleResize = () => {
      canvas.width = window.innerWidth
      canvas.height = window.innerHeight
    }

    window.addEventListener("resize", handleResize)

    return () => {
      clearInterval(interval)
      window.removeEventListener("resize", handleResize)
    }
  }, [darkMode])

  return <canvas ref={canvasRef} className="matrix-canvas"></canvas>
}

export default App
